import * as React from 'react';
import { ExternalLink, Lock, Plus, Trash2 } from 'lucide-react';
import {
  SOCIAL_PLATFORMS,
  type AgentProptreeSettings,
  type ProfileButton,
  type SocialLink,
  type SocialPlatform,
} from '@proptr/shared';
import { AppShell } from '../components/AppShell.js';
import { AddButtonModal } from '../components/AddButtonModal.js';
import { Card } from '../components/ui/card.js';
import { Button } from '../components/ui/button.js';
import {
  Field,
  Label,
  ProptreeFormSkeleton,
  SaveBar,
  SectionHeader,
  Switch,
} from '../components/proptreeForm.js';
import { client } from '../main.js';

// In dev, link the public proptree to the local landing app (port 4040)
// so OPEN actually opens something the developer can preview. Override
// with VITE_PUBLIC_URL when you want a different target (staging, etc).
const PUBLIC_URL =
  (import.meta.env.VITE_PUBLIC_URL as string | undefined) ??
  (import.meta.env.DEV ? 'http://localhost:4040' : 'https://proptr.ee');

const SOCIAL_LABELS: Record<SocialPlatform, string> = {
  instagram: 'Instagram',
  linkedin: 'LinkedIn',
  facebook: 'Facebook',
  tiktok: 'TikTok',
  youtube: 'YouTube',
  twitter: 'X (Twitter)',
  snapchat: 'Snapchat',
};

// Small placeholder hint per platform — matches what the legacy agent
// portal showed under the username field.
const SOCIAL_PLACEHOLDERS: Record<SocialPlatform, string> = {
  instagram: '/username',
  linkedin: '/in/username',
  facebook: '/username',
  tiktok: '@username',
  youtube: '/@channel or /channel/...',
  twitter: '/username',
  snapchat: '/add/username',
};

type LoadStatus =
  | { kind: 'loading' }
  | { kind: 'ready' }
  | { kind: 'no-proptree' }
  | { kind: 'error'; message: string };

export function MyProptreePage() {
  const [status, setStatus] = React.useState<LoadStatus>({ kind: 'loading' });
  const [data, setData] = React.useState<AgentProptreeSettings | null>(null);
  const [original, setOriginal] = React.useState<AgentProptreeSettings | null>(
    null,
  );
  const [saving, setSaving] = React.useState(false);
  const [saveError, setSaveError] = React.useState<string | null>(null);
  const [savedAt, setSavedAt] = React.useState<Date | null>(null);

  React.useEffect(() => {
    let alive = true;
    client.agents
      .getMyProptree()
      .then((s) => {
        if (!alive) return;
        setData(s);
        setOriginal(s);
        setStatus({ kind: 'ready' });
      })
      .catch((err: Error & { code?: string }) => {
        if (!alive) return;
        if (err.message?.includes('NO_AGENT_PROPTREE') || /404/.test(err.message)) {
          setStatus({ kind: 'no-proptree' });
        } else {
          setStatus({ kind: 'error', message: err.message });
        }
      });
    return () => {
      alive = false;
    };
  }, []);

  const dirty = React.useMemo(() => {
    if (!data || !original) return false;
    return JSON.stringify(data) !== JSON.stringify(original);
  }, [data, original]);

  const handleSave = async () => {
    if (!data || saving) return;
    setSaving(true);
    setSaveError(null);
    try {
      const updated = await client.agents.updateMyProptree({
        alias: data.alias ?? undefined,
        name: data.name ?? undefined,
        recoveryEmail: data.recoveryEmail ?? '',
        social: data.social,
        buttons: data.buttons.map((b) => ({
          id: b.id,
          title: b.title,
          subtitle: b.subtitle,
          url: b.url,
          icon: b.icon,
          image: b.image,
          images: b.images,
          enabled: b.enabled,
          type: b.type,
        })),
        notifications: data.notifications,
      });
      setData(updated);
      setOriginal(updated);
      setSavedAt(new Date());
    } catch (err) {
      setSaveError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppShell>
      <div className="space-y-8 pb-32">
        <div className="flex flex-wrap items-start justify-between gap-6 border-b border-[hsl(var(--hairline))] pb-6">
          <div className="min-w-0">
            <h1 className="font-display text-[40px] font-medium leading-none tracking-[-0.025em]">
              My proptree
            </h1>
            <p className="mt-3 font-mono text-[11px] text-muted-foreground">
              Edit the public face of your proptree — link, profile, socials,
              and how we notify you.
            </p>
          </div>
          {status.kind === 'ready' && data?.alias && (
            <OpenProptreeCard alias={data.alias} />
          )}
        </div>

        {status.kind === 'loading' && <ProptreeFormSkeleton />}

        {status.kind === 'no-proptree' && (
          <Card className="p-8 text-center">
            <h2 className="font-display text-[24px] font-medium tracking-[-0.02em]">
              No proptree yet
            </h2>
            <p className="mx-auto mt-3 max-w-md font-mono text-[12px] leading-relaxed text-muted-foreground">
              Your manager hasn't created a proptree for you yet. Once they
              do, your editable settings will appear here.
            </p>
          </Card>
        )}

        {status.kind === 'error' && (
          <Card className="p-6">
            <p className="font-mono text-[12px] text-destructive">
              {status.message}
            </p>
          </Card>
        )}

        {status.kind === 'ready' && data && (
          <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_400px]">
            <div className="min-w-0 space-y-8">
              <ProfileCard data={data} onChange={setData} />
              <ButtonsCard data={data} onChange={setData} />
              <SocialCard data={data} onChange={setData} />
            </div>
            {original?.alias && (
              <ProptreePreview
                alias={original.alias}
                version={savedAt?.getTime() ?? 0}
              />
            )}
          </div>
        )}
      </div>

      {status.kind === 'ready' && (
        <SaveBar
          dirty={dirty}
          saving={saving}
          error={saveError}
          savedAt={savedAt}
          onSave={handleSave}
        />
      )}
    </AppShell>
  );
}

// Right-column preview shown on desktop. Iframes the public proptree
// page so the agent can see what visitors see. Re-keys on `version` so
// it reloads after each save instead of staying stale.
function ProptreePreview({
  alias,
  version,
}: {
  alias: string;
  version: number;
}) {
  const url = `${PUBLIC_URL}/${alias}`;
  return (
    <div className="hidden lg:sticky lg:top-24 lg:block lg:self-start">
      <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
        <div className="flex items-center justify-between gap-2 border-b border-[hsl(var(--hairline))] bg-muted/40 px-3 py-2">
          <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
            Live preview
          </span>
          <span className="truncate font-mono text-[10px] text-muted-foreground">
            {url.replace(/^https?:\/\//, '')}
          </span>
        </div>
        <iframe
          key={version}
          src={url}
          title="Proptree preview"
          className="h-[calc(100vh-12rem)] w-full bg-background"
        />
      </div>
    </div>
  );
}

// Compact card shown next to the page title — opens the public proptree
// page in a new tab. Mirrors the manager portal's `SettingsCard` shape
// (300px wide, title + subtitle + button on the right) so the agent
// portal feels coherent with the manager.
function OpenProptreeCard({ alias }: { alias: string }) {
  const url = `${PUBLIC_URL}/${alias}`;
  return (
    <Card className="flex w-[300px] items-center gap-3 px-4 py-3">
      <div className="min-w-0 flex-1">
        <div className="font-sans text-[13px] font-semibold leading-tight text-foreground">
          Your proptree
        </div>
        <p className="mt-1 truncate font-mono text-[11px] leading-snug text-muted-foreground">
          {url.replace(/^https?:\/\//, '')}
        </p>
      </div>
      <Button asChild size="sm" variant="outline">
        <a href={url} target="_blank" rel="noopener noreferrer">
          <ExternalLink />
          Open
        </a>
      </Button>
    </Card>
  );
}

// ---- Sections ----

function ProfileCard({
  data,
  onChange,
}: {
  data: AgentProptreeSettings;
  onChange: (next: AgentProptreeSettings) => void;
}) {
  const [copied, setCopied] = React.useState(false);
  const link = `${PUBLIC_URL}/${data.alias ?? ''}`;
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // ignore — clipboard not available
    }
  };

  return (
    <Card className="space-y-6 p-6">
      <SectionHeader
        title="Profile"
        description="Your profile is how visitors recognise you. Keep it current."
      />

      <div className="grid grid-cols-1 items-start gap-6 md:grid-cols-[160px_1fr]">
        <ProfileImageEditor
          value={data.profileImage}
          name={data.name ?? null}
          onChange={(url) => onChange({ ...data, profileImage: url })}
        />

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>
              Your proptree link{' '}
              <button
                type="button"
                onClick={onCopy}
                className="ml-1 font-mono text-[10px] text-primary hover:underline"
              >
                {copied ? 'Copied!' : '(click to copy)'}
              </button>
            </Label>
            <div className="flex">
              <span className="inline-flex items-center rounded-l-md border border-r-0 border-border bg-muted px-3 font-mono text-[12px] text-muted-foreground">
                {PUBLIC_URL.replace(/^https?:\/\//, '')}/
              </span>
              <input
                value={data.alias ?? ''}
                onChange={(e) =>
                  onChange({ ...data, alias: e.target.value })
                }
                placeholder="your-link"
                spellCheck={false}
                autoComplete="off"
                className="flex-1 rounded-r-md border border-border bg-card px-3 py-2 font-mono text-[13px] focus:border-transparent focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1 focus:ring-offset-background dark:border-white/10 dark:bg-card-raised"
              />
            </div>
            <AliasAvailability alias={data.alias ?? ''} />
          </div>

          <Field
            label="Name"
            value={data.name ?? ''}
            onChange={(v) => onChange({ ...data, name: v })}
            placeholder="Your full name"
          />

          <div className="space-y-1.5">
            <Label>Company</Label>
            <div className="relative">
              <input
                value={data.company ?? ''}
                disabled
                className="w-full rounded-md border border-border bg-muted/40 px-3 py-2 pr-10 font-sans text-[13px] text-muted-foreground"
              />
              <Lock className="pointer-events-none absolute right-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            </div>
            <p className="font-mono text-[10px] text-muted-foreground">
              Set by your manager.
            </p>
          </div>
        </div>
      </div>
    </Card>
  );
}

function SocialCard({
  data,
  onChange,
}: {
  data: AgentProptreeSettings;
  onChange: (next: AgentProptreeSettings) => void;
}) {
  const setLink = (platform: SocialPlatform, link: SocialLink) => {
    onChange({
      ...data,
      social: { ...data.social, [platform]: link },
    });
  };

  return (
    <Card className="space-y-6 p-6">
      <SectionHeader
        title="Social links"
        description="Surface the channels you actually use. Toggle off to hide a row."
      />
      <div className="space-y-3">
        {SOCIAL_PLATFORMS.map((platform) => {
          const link = data.social[platform];
          return (
            <div
              key={platform}
              className="flex items-center gap-3 rounded-md border border-border bg-card px-3 py-2 dark:border-white/10 dark:bg-card-raised"
            >
              <span className="w-24 shrink-0 font-mono text-[11px] font-semibold uppercase tracking-[0.06em] text-foreground">
                {SOCIAL_LABELS[platform]}
              </span>
              <input
                value={link.username}
                onChange={(e) =>
                  setLink(platform, {
                    ...link,
                    username: e.target.value,
                  })
                }
                placeholder={SOCIAL_PLACEHOLDERS[platform]}
                spellCheck={false}
                autoComplete="off"
                className="flex-1 bg-transparent font-mono text-[13px] text-foreground placeholder:text-muted-foreground/70 focus:outline-none"
              />
              <Switch
                checked={link.enabled}
                onChange={(v) => setLink(platform, { ...link, enabled: v })}
                ariaLabel={`Show ${SOCIAL_LABELS[platform]} on your proptree`}
              />
            </div>
          );
        })}
      </div>
    </Card>
  );
}

function ButtonsCard({
  data,
  onChange,
}: {
  data: AgentProptreeSettings;
  onChange: (next: AgentProptreeSettings) => void;
}) {
  const [modalOpen, setModalOpen] = React.useState(false);

  const setButtons = (buttons: ProfileButton[]) =>
    onChange({ ...data, buttons });

  const updateAt = (i: number, patch: Partial<ProfileButton>) =>
    setButtons(
      data.buttons.map((b, j) => (j === i ? { ...b, ...patch } : b)),
    );

  const removeAt = (i: number) =>
    setButtons(data.buttons.filter((_, j) => j !== i));

  return (
    <Card className="space-y-6 p-6">
      <SectionHeader
        title="Custom buttons"
        description="Surface ratings, reviews, videos, offers, anything you want one tap away."
      />
      {data.buttons.length === 0 ? (
        <div className="rounded-md border border-dashed border-border bg-card/40 p-6 text-center font-mono text-[11px] text-muted-foreground">
          No custom buttons yet.
        </div>
      ) : (
        <div className="space-y-3">
          {data.buttons.map((b, i) => (
            <ButtonRow
              key={b.id}
              button={b}
              onChange={(patch) => updateAt(i, patch)}
              onRemove={() => removeAt(i)}
            />
          ))}
        </div>
      )}
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => setModalOpen(true)}
        disabled={data.buttons.length >= 20}
      >
        <Plus />
        Add button
      </Button>
      <AddButtonModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onAdd={(button) => setButtons([...data.buttons, button])}
      />
    </Card>
  );
}

const BUTTON_TYPE_LABEL: Record<string, string> = {
  link: 'Link',
  video: 'Video',
  gallery: 'Gallery',
};

// Inline editor for an existing button. URL is hidden for galleries (the
// gallery has no destination), and relabelled for video. Subtitle is
// always editable so the user can tweak it without re-creating the button.
function ButtonRow({
  button,
  onChange,
  onRemove,
}: {
  button: ProfileButton;
  onChange: (patch: Partial<ProfileButton>) => void;
  onRemove: () => void;
}) {
  const type = button.type || 'link';
  const showUrl = type !== 'gallery';
  const urlPlaceholder = type === 'video' ? 'Video URL (mp4 or YouTube)' : 'https://…';

  return (
    <div className="space-y-2 rounded-md border border-border bg-card p-3 dark:border-white/10 dark:bg-card-raised">
      <div className="flex items-center gap-2">
        <span className="rounded-full bg-muted px-2 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
          {BUTTON_TYPE_LABEL[type] ?? type}
        </span>
        {button.image ? (
          <img
            src={button.image}
            alt=""
            className="h-6 w-6 shrink-0 rounded-full border border-border object-cover"
          />
        ) : button.icon ? (
          <span className="flex h-6 w-6 shrink-0 items-center justify-center text-[16px] leading-none">
            {button.icon}
          </span>
        ) : null}
        <input
          value={button.title}
          onChange={(e) => onChange({ title: e.target.value })}
          placeholder="Title"
          className="flex-1 rounded-md border border-border bg-card px-3 py-1.5 font-sans text-[13px] focus:border-transparent focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1 focus:ring-offset-background dark:border-white/10 dark:bg-card-raised"
        />
        <Switch
          checked={button.enabled}
          onChange={(v) => onChange({ enabled: v })}
          ariaLabel={`Enable button ${button.title || ''}`}
        />
        <button
          type="button"
          onClick={onRemove}
          className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
          aria-label={`Remove button ${button.title || ''}`}
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
      <input
        value={button.subtitle ?? ''}
        onChange={(e) => onChange({ subtitle: e.target.value || null })}
        placeholder="Subtitle (optional)"
        className="w-full rounded-md border border-border bg-card px-3 py-1.5 font-sans text-[12px] focus:border-transparent focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1 focus:ring-offset-background dark:border-white/10 dark:bg-card-raised"
      />
      {showUrl && (
        <input
          value={button.url}
          onChange={(e) => onChange({ url: e.target.value })}
          placeholder={urlPlaceholder}
          spellCheck={false}
          autoComplete="off"
          className="w-full rounded-md border border-border bg-card px-3 py-1.5 font-mono text-[12px] focus:border-transparent focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1 focus:ring-offset-background dark:border-white/10 dark:bg-card-raised"
        />
      )}
      {type === 'gallery' && (button.images?.length ?? 0) > 0 && (
        <div className="flex gap-1.5 overflow-x-auto">
          {button.images!.map((src, i) => (
            <img
              key={i}
              src={src}
              alt=""
              className="h-12 w-12 shrink-0 rounded-md object-cover"
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ---- Profile image editor (Cloudinary signed direct upload) ----

function ProfileImageEditor({
  value,
  name,
  onChange,
}: {
  value: string | null;
  name: string | null;
  onChange: (url: string) => void;
}) {
  const fileRef = React.useRef<HTMLInputElement>(null);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const handleFile = async (file: File) => {
    setBusy(true);
    setError(null);
    try {
      const sig = await client.agents.getProfileImageSignature();
      const form = new FormData();
      form.append('file', file);
      form.append('api_key', sig.apiKey);
      form.append('timestamp', String(sig.timestamp));
      form.append('folder', sig.folder);
      form.append('signature', sig.signature);

      const res = await fetch(
        `https://api.cloudinary.com/v1_1/${sig.cloudName}/image/upload`,
        { method: 'POST', body: form },
      );
      if (!res.ok) {
        const body = await res.text().catch(() => '');
        throw new Error(`Cloudinary returned ${res.status}: ${body.slice(0, 120)}`);
      }
      const json = (await res.json()) as { secure_url?: string };
      if (!json.secure_url) throw new Error('No URL returned from Cloudinary');

      // Match legacy's auto-resize transform — c_scale,w_1200 keeps the
      // image small enough to render fast on the public proptree page.
      const scaled = json.secure_url.replace(
        /\/upload\//,
        '/upload/c_scale,w_1200/',
      );
      onChange(scaled);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div className="flex flex-col items-center gap-3">
      <div className="h-32 w-32 overflow-hidden rounded-full border border-border bg-muted">
        {value ? (
          <img
            src={value}
            alt={name ?? 'Profile'}
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center font-mono text-[10px] uppercase tracking-[0.08em] text-muted-foreground">
            No image
          </div>
        )}
      </div>
      <input
        ref={fileRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) void handleFile(file);
        }}
      />
      <button
        type="button"
        onClick={() => fileRef.current?.click()}
        disabled={busy}
        className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-3 py-1.5 font-mono text-[11px] font-medium uppercase tracking-[0.04em] text-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-60 dark:border-white/10 dark:bg-card-raised"
      >
        {busy ? 'Uploading…' : value ? 'Change image' : 'Upload image'}
      </button>
      {error && (
        <p className="max-w-[160px] text-center font-mono text-[10px] text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}

// ---- Live alias availability (debounced) ----

type AliasStatus =
  | { kind: 'idle' }
  | { kind: 'invalid' }
  | { kind: 'checking' }
  | { kind: 'available' | 'same' }
  | { kind: 'taken' }
  | { kind: 'error' };

function AliasAvailability({ alias }: { alias: string }) {
  const [status, setStatus] = React.useState<AliasStatus>({ kind: 'idle' });

  React.useEffect(() => {
    const trimmed = alias.trim();
    if (!trimmed) {
      setStatus({ kind: 'idle' });
      return;
    }
    if (!/^[a-zA-Z0-9_-]+$/.test(trimmed) || trimmed.length > 30) {
      setStatus({ kind: 'invalid' });
      return;
    }
    setStatus({ kind: 'checking' });
    let cancelled = false;
    const t = setTimeout(() => {
      client.agents
        .checkAlias(trimmed)
        .then((res) => {
          if (cancelled) return;
          if (res.available) {
            setStatus({ kind: res.reason === 'same' ? 'same' : 'available' });
          } else {
            setStatus({
              kind: res.reason === 'invalid' ? 'invalid' : 'taken',
            });
          }
        })
        .catch(() => {
          if (!cancelled) setStatus({ kind: 'error' });
        });
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [alias]);

  if (status.kind === 'idle') {
    return (
      <p className="font-mono text-[10px] text-muted-foreground">
        Letters, digits, dashes and underscores only.
      </p>
    );
  }
  if (status.kind === 'invalid') {
    return (
      <p className="font-mono text-[10px] text-destructive">
        Only letters, digits, _ and - (max 30).
      </p>
    );
  }
  if (status.kind === 'checking') {
    return (
      <p className="font-mono text-[10px] text-muted-foreground">
        Checking availability…
      </p>
    );
  }
  if (status.kind === 'taken') {
    return (
      <p className="font-mono text-[10px] text-destructive">
        That link is already taken.
      </p>
    );
  }
  if (status.kind === 'error') {
    return (
      <p className="font-mono text-[10px] text-muted-foreground">
        Couldn't check availability. We'll re-check on save.
      </p>
    );
  }
  // available / same
  return (
    <p className="font-mono text-[10px] text-success">
      {status.kind === 'same' ? 'Your current link.' : 'Available.'}
    </p>
  );
}
